/*****************************************************
 * Packages
 ******************************************************/
import styled from 'styled-components';
import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
/*****************************************************
 * Locals
 ******************************************************/
import Alert from '../Alert';
import Label from '../Label';
import Field from '../Field';
import Input from '../Input';
import Modal from '../Modal';
import ucFirst from '../../util/ucFirst';
import Radio, { RadioGroup } from '../Radio';
import * as actions from '../../redux/actions';
import strBoolean from '../../util/strBoolean';
import { capitalize } from '../../util/capitalize';
import Button from '../other-components/Button';
import { getAuthUser, getIsAdmin } from '../../redux/selectors';
import ImagePreview from '../setting-components/ImagePreview';

const ModalFooter = styled.div`
    display: flex;
    justify-content: space-between;
`;
const ModalHeader = styled.div`
    width: 100%;
    font-size: 18px;
    font-weight: 500;
    text-align: center;
`;

const SaveElementModal = ({ onClose, _elType, data }) => {
    const dispatch = useDispatch();
    const user = useSelector(getAuthUser);
    const isAdmin = useSelector(getIsAdmin);
    const [title, setTitle] = React.useState('');
    const [isPrivate, setPrivate] = React.useState(true);
    const [tags, setTags] = React.useState('');
    const [thumbnail, setThumbnail] = React.useState('');
    const handleTitleChange = React.useCallback(
        (e) => setTitle(e.target.value),
        []
    );
    const handleSave = React.useCallback(() => {
        const tagsArray = tags ? tags.split(',').map(ucFirst) : undefined;
        dispatch(
            actions.saveElement({
                title,
                tags: tagsArray,
                isPrivate,
                thumbnail,
                user: user?.id,
                type: _elType,
                data: JSON.stringify(data),
            })
        );
        onClose();
    }, [
        tags,
        dispatch,
        title,
        isPrivate,
        thumbnail,
        user,
        _elType,
        data,
        onClose,
    ]);

    const handleUpload = useCallback(({ value }) => {
        setThumbnail(value);
    }, []);

    return (
        <Modal
            size="sm"
            dropShadow
            onClose={onClose}
            header={
                <ModalHeader>{capitalize(`Save ${data.type}`)}</ModalHeader>
            }
            footer={
                <ModalFooter>
                    <Button size="md" onClick={handleSave} disabled={!user}>
                        Save To Library
                    </Button>
                    <Button size="md" type="none" onClick={onClose}>
                        Cancel
                    </Button>
                </ModalFooter>
            }
        >
            <Field>
                <Label>Name</Label>
                <Input value={title} size="sm" onChange={handleTitleChange} />
            </Field>
            {isAdmin && (
                <>
                    <Field>
                        <Label>Tags</Label>
                        <Input
                            value={tags}
                            size="sm"
                            onChange={(e) => setTags(e.target.value)}
                        />
                    </Field>
                    <Field>
                        <Label>Thumbnail</Label>
                        <ImagePreview
                            accept="image/*"
                            value={thumbnail}
                            onChange={handleUpload}
                        />
                    </Field>
                    <Field>
                        <Label>Make public</Label>
                        <RadioGroup
                            name="type"
                            type="inline"
                            value={isPrivate}
                            onChange={(e) =>
                                setPrivate(strBoolean(e.target.value))
                            }
                        >
                            <Radio value={false}>Yes</Radio>
                            <Radio value={true}>No</Radio>
                        </RadioGroup>
                    </Field>
                </>
            )}
            {!user && (
                <Alert type="info">
                    Please login to save into your library
                </Alert>
            )}
        </Modal>
    );
};

export default React.memo(SaveElementModal);
